// src/Pages/EditJob.jsx
import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, updateDoc,
  collection, getDocs, query, where
} from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL
} from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import Sidebar from '../Components/Sidebar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaPlus, FaTrashAlt, FaCloudUploadAlt, FaSpinner
} from 'react-icons/fa';
import '../Style/EditJob.css';

/* ───────── helpers ───────── */
const fmtDate = d => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  if (d.toDate) return d.toDate().toISOString().slice(0, 10);
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  return '';
};

const debounce = (fn, wait = 300) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

const isImage = name => /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name);
const isPdf   = name => /\.pdf$/i.test(name);
const isCsv   = name => /\.csv$/i.test(name);

/* ───────── component ───────── */
export default function EditJob() {
  const { id } = useParams();
  const navigate = useNavigate();

  /* data state */
  const [originalJob, setOriginalJob] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [rfqs, setRfqs] = useState([]);                 // all RFQs (any user, any status)

  /* ui state */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dragActive, setDrag] = useState(false);
  const fileInputRef = useRef(null);

  /* form state */
  const [formData, setFormData] = useState({
    jobId: '', jobName: '', status: 'Draft',
    rfq: '',                                           // selected RFQ id
    customer: '',
    description: '',
    tasks: [],
    expenses: [],
    fileUrls: []
  });
  const [newFiles, setNewFiles] = useState([]);      // new File objects
  const [previews, setPreviews] = useState([]);      // [{ url, name, existing }]
  const [errors, setErrors] = useState({
    jobId: false, jobName: false, status: false, rfq: false
  });

  /* totals */
  const totals = useMemo(() => {
    const baseTotal = formData.expenses
      .reduce((s, e) => s + ((parseFloat(e.qty) || 0) * (parseFloat(e.cost) || 0)), 0);
    const finalTotal = formData.expenses
      .reduce((s, e) => {
        const qty = parseFloat(e.qty) || 0;
        const cost = parseFloat(e.cost) || 0;
        const markup = parseFloat(e.markup) || 0;
        return s + (qty * cost * (1 + markup / 100));
      }, 0);
    return {
      base: baseTotal.toFixed(2),
      final: finalTotal.toFixed(2)
    };
  }, [formData.expenses]);

  /* ───────── fetch job + lists ───────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Job
        const jobSnap = await getDoc(doc(db, 'Jobs', id));
        if (!jobSnap.exists()) {
          toast.error('Job not found.', { className: 'toast-error' });
          navigate('/jobs');
          return;
        }
        const jobData = { docId: jobSnap.id, ...jobSnap.data() };
        setOriginalJob(jobData);

        // Customers
        const custSnap = await getDocs(collection(db, 'Customers'));
        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Suppliers
        const suppSnap = await getDocs(collection(db, 'Suppliers'));
        setSuppliers(suppSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // ALL RFQs (no status filter, any user)
        const rfqSnap = await getDocs(collection(db, 'RFQs'));
        setRfqs(rfqSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // build form
        setFormData({
          jobId: jobData.jobId || '',
          jobName: jobData.jobName || '',
          status: jobData.status || 'Draft',
          rfq: jobData.linkedRfqId || jobData.rfq || '',
          customer: jobData.customer || '',
          description: jobData.description || '',
          tasks: jobData.tasks || [],
          expenses: (jobData.expenses || []).map(e => ({
            supplier: e.supplier || e.name || '',
            description: e.description === '—' ? '' : (e.description || ''),
            qty: e.qty === '—' ? '' : e.qty,
            cost: e.cost === '—' ? '' : e.cost,
            markup: e.markup === '—' ? '' : e.markup,
            base: (parseFloat(e.qty) || 0) * (parseFloat(e.cost) || 0),
            total: e.total || ((parseFloat(e.qty) || 0) * (parseFloat(e.cost) || 0) * (1 + (parseFloat(e.markup) || 0)/100))
          })),
          fileUrls: jobData.fileUrls || []
        });

        setPreviews((jobData.fileUrls || []).map(u => ({
          url: u, name: u.split('?')[0].split('/').pop(), existing: true
        })));
      } catch (err) {
        console.error(err);
        toast.error('Error loading job.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  /* ───────── validation ───────── */
  const validate = useCallback(() => {
    const newErrors = {
      jobId: !formData.jobId.trim(),
      jobName: !formData.jobName.trim(),
      status: !formData.status,
      rfq: !formData.rfq
    };
    setErrors(newErrors);
    return !newErrors.jobId && !newErrors.jobName && !newErrors.status && !newErrors.rfq;
  }, [formData.jobId, formData.jobName, formData.status, formData.rfq]);

  /* ───────── debounced setters ───────── */
  const debouncedSetField = useMemo(() => debounce((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({
      ...prev,
      [name]: name === 'status' ? !value : !String(value).trim()
    }));
  }, 300), []);

  const handleField = (name, value) => debouncedSetField(name, value);

  /* ───────── tasks handlers ───────── */
  const changeTask = useCallback((i, field, value) => {
    setFormData(p => {
      const tasks = [...p.tasks];
      tasks[i] = { ...tasks[i], [field]: value };
      return { ...p, tasks };
    });
  }, []);
  const addTask = () => setFormData(p => ({
    ...p, tasks: [...p.tasks, { name: '', description: '', date: '', hours: '' }]
  }));
  const removeTask = i => setFormData(p => ({ ...p, tasks: p.tasks.filter((_, idx) => idx !== i) }));

  /* ───────── expenses handlers ───────── */
  const changeExpense = useCallback((i, field, value) => {
    setFormData(p => {
      const expenses = [...p.expenses];
      const row = { ...expenses[i], [field]: value };

      const qty = parseFloat(field === 'qty' ? value : row.qty) || 0;
      const cost = parseFloat(field === 'cost' ? value : row.cost) || 0;
      const markup = parseFloat(field === 'markup' ? value : row.markup) || 0;

      row.base = qty * cost;
      row.total = qty * cost * (1 + markup / 100);
      expenses[i] = row;
      return { ...p, expenses };
    });
  }, []);
  const addExpense = () => setFormData(p => ({
    ...p,
    expenses: [...p.expenses, {
      supplier: '', description: '', qty: '', cost: '', markup: '', base: 0, total: 0
    }]
  }));
  const removeExpense = i =>
    setFormData(p => ({ ...p, expenses: p.expenses.filter((_, idx) => idx !== i) }));

  /* ───────── file handling ───────── */
  const addFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;

    const valid = [];
    for (const f of arr) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} > 5MB skipped`, { className: 'toast-error' });
        continue;
      }
      valid.push(f);
    }

    const totalCount = previews.length + valid.length;
    if (totalCount > 10) {
      toast.error('Max 10 files total.', { className: 'toast-error' });
      return;
    }

    const pv = valid.map(f => ({
      url: URL.createObjectURL(f),
      name: f.name,
      existing: false
    }));

    setNewFiles(p => [...p, ...valid]);
    setPreviews(p => [...p, ...pv]);
  }, [previews.length]);

  const openFileDialog = () => fileInputRef.current?.click();
  const onFileInput = e => addFiles(e.target.files);
  const onDragOver = e => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);
  const onDrop = e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); };

  const removeFile = index => {
    const prev = previews[index];
    if (prev.existing) {
      setFormData(p => ({
        ...p,
        fileUrls: p.fileUrls.filter(u => u !== prev.url)
      }));
    } else {
      const newIndex = previews.slice(0, index).filter(p => !p.existing).length;
      setNewFiles(f => f.filter((_, i) => i !== newIndex));
    }
    setPreviews(p => {
      const copy = [...p];
      if (!copy[index].existing && copy[index].url.startsWith('blob:')) {
        URL.revokeObjectURL(copy[index].url);
      }
      copy.splice(index, 1);
      return copy;
    });
  };

  /* ───────── upload helper ───────── */
  const uploadNewFiles = useCallback(async () => {
    if (newFiles.length === 0) return { fileNames: [], fileUrls: formData.fileUrls };
    const uploaded = await Promise.all(
      newFiles.map(async f => {
        const storageRef = ref(storage, `Jobs/${formData.jobId}/${f.name}`);
        await uploadBytes(storageRef, f);
        return { name: f.name, url: await getDownloadURL(storageRef) };
      })
    );
    return {
      fileNames: [
        ...formData.fileUrls.map(u => u.split('?')[0].split('/').pop()),
        ...uploaded.map(u => u.name)
      ],
      fileUrls: [...formData.fileUrls, ...uploaded.map(u => u.url)]
    };
  }, [newFiles, formData.jobId, formData.fileUrls]);

  /* ───────── clear / reset ───────── */
  const clearForm = () => {
    if (!originalJob) return;
    setFormData({
      jobId: originalJob.jobId || '',
      jobName: originalJob.jobName || '',
      status: originalJob.status || 'Draft',
      rfq: originalJob.linkedRfqId || originalJob.rfq || '',
      customer: originalJob.customer || '',
      description: originalJob.description || '',
      tasks: originalJob.tasks || [],
      expenses: (originalJob.expenses || []).map(e => ({
        supplier: e.supplier || e.name || '',
        description: e.description === '—' ? '' : (e.description || ''),
        qty: e.qty === '—' ? '' : e.qty,
        cost: e.cost === '—' ? '' : e.cost,
        markup: e.markup === '—' ? '' : e.markup,
        base: (parseFloat(e.qty) || 0) * (parseFloat(e.cost) || 0),
        total: e.total || ((parseFloat(e.qty) || 0) * (parseFloat(e.cost) || 0) * (1 + (parseFloat(e.markup) || 0)/100))
      })),
      fileUrls: originalJob.fileUrls || []
    });
    setNewFiles([]);
    setPreviews((originalJob.fileUrls || []).map(u => ({
      url: u, name: u.split('?')[0].split('/').pop(), existing: true
    })));
    setErrors({ jobId: false, jobName: false, status: false, rfq: false });
  };

  /* ───────── submit ───────── */
  const submit = async e => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fill required fields.', { className: 'toast-error' });
      return;
    }
    setSaving(true);
    try {
      const { fileNames, fileUrls } = await uploadNewFiles();

      const rfqObj = rfqs.find(r => r.id === formData.rfq);

      await updateDoc(doc(db, 'Jobs', id), {
        jobId: formData.jobId.trim(),
        jobName: formData.jobName.trim(),
        status: formData.status,
        linkedRfqId: formData.rfq,
        linkedRfqNo: rfqObj?.rfqNo || rfqObj?.rfqId || '',
        customer: formData.customer,
        description: formData.description.trim() || '—',
        tasks: formData.tasks.map(t => ({
          name: t.name?.trim() || '—',
          description: t.description?.trim() || '—',
          date: t.date || '—',
          hours: t.hours || '—'
        })),
        expenses: formData.expenses.map(e => {
          const qty = parseFloat(e.qty) || 0;
          const cost = parseFloat(e.cost) || 0;
          const markup = parseFloat(e.markup) || 0;
          return {
            supplier: e.supplier || '—',
            name: e.supplier || '—',
            description: e.description?.trim() || '—',
            qty: e.qty || '—',
            cost: e.cost || '—',
            markup: e.markup || '—',
            base: Number((qty * cost).toFixed(2)),
            total: Number((qty * cost * (1 + markup / 100)).toFixed(2))
          };
        }),
        fileNames,
        fileUrls,
        grandTotalBase: Number(totals.base),
        grandTotal: Number(totals.final),
        updatedAt: new Date().toISOString()
      });

      toast.success('Job updated successfully!', { className: 'toast-success' });
      navigate('/jobs');
    } catch (err) {
      console.error(err);
      toast.error('Update failed.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  /* ───────── navigation ───────── */
  const cancel = () => navigate('/jobs');

  /* ───────── cleanup blobs ───────── */
  useEffect(() => {
    return () => {
      previews.forEach(p => {
        if (!p.existing && p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
      });
    };
  }, [previews]);

  /* ───────── render ───────── */
  if (loading) {
    return (
      <div className="editjob-page">
        <Sidebar />
        <div className="editjob-loading">
          <FaSpinner className="spin" size={48}/>
          <p>Loading job data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="editjob-page">
      <ToastContainer
        position="top-right"
        autoClose={2600}
        toastClassName={({ type }) =>
          type === 'success' ? 'toast-success' : 'toast-error'
        }
      />
      <Sidebar />
      <main className="editjob-main">
        <h2>Edit Job</h2>
        <form className="editjob-form" onSubmit={submit}>
          {/* Basic Info */}
          <section className="panel">
            <div className="field-row four-cols">
              <div className="field-group">
                <label>Job ID <span className="required">*</span></label>
                <input
                  value={formData.jobId}
                  onChange={e => handleField('jobId', e.target.value)}
                  className={errors.jobId ? 'input-error' : ''}
                  aria-invalid={errors.jobId}
                  aria-describedby={errors.jobId ? 'jobid-error' : undefined}
                />
                {errors.jobId && <span id="jobid-error" className="error-text">Required</span>}
              </div>

              <div className="field-group">
                <label>Job Name <span className="required">*</span></label>
                <input
                  value={formData.jobName}
                  onChange={e => handleField('jobName', e.target.value)}
                  className={errors.jobName ? 'input-error' : ''}
                  aria-invalid={errors.jobName}
                  aria-describedby={errors.jobName ? 'jobname-error' : undefined}
                />
                {errors.jobName && <span id="jobname-error" className="error-text">Required</span>}
              </div>

              {/* RFQ select */}
              <div className="field-group">
                <label>RFQ <span className="required">*</span></label>
                <select
                  value={formData.rfq}
                  onChange={e => handleField('rfq', e.target.value)}
                  className={errors.rfq ? 'input-error' : ''}
                  aria-invalid={errors.rfq}
                  aria-describedby={errors.rfq ? 'rfq-error' : undefined}
                >
                  <option value="">Select RFQ</option>
                  {rfqs.map(r => (
                    <option key={r.id} value={r.id}>
                      {(r.rfqNo || r.rfqId || r.id)} {r.status ? `- ${r.status}` : ''}
                    </option>
                  ))}
                </select>
                {errors.rfq && <span id="rfq-error" className="error-text">Required</span>}
              </div>

              <div className="field-group">
                <label>Status <span className="required">*</span></label>
                <select
                  value={formData.status}
                  onChange={e => handleField('status', e.target.value)}
                  className={errors.status ? 'input-error' : ''}
                  aria-invalid={errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                >
                  {['Draft','Pending','In Progress','On Hold','Completed','Overdue','Cancelled']
                    .map(s => <option key={s}>{s}</option>)}
                </select>
                {errors.status && <span id="status-error" className="error-text">Required</span>}
              </div>
            </div>

            <div className="field-row three-cols">
              <div className="field-group">
                <label>Customer</label>
                <select
                  value={formData.customer}
                  onChange={e => handleField('customer', e.target.value)}
                >
                  <option value="">Select customer</option>
                  {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="field-group full-span">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={e => handleField('description', e.target.value)}
                  placeholder="General job description…"
                />
              </div>
            </div>
          </section>

          {/* Tasks */}
          <section className="panel">
            <div className="section-head">
              <h3>Tasks</h3>
              <button type="button" className="btn-inline-add" onClick={addTask}>
                <FaPlus/> Add Task
              </button>
            </div>
            <div className="tasks-header">
              <div>Name</div>
              <div>Description</div>
              <div>Date</div>
              <div>Hours</div>
              <div></div>
            </div>
            {formData.tasks.map((t, i) => (
              <div className="task-row" key={i}>
                <input
                  placeholder="Name"
                  value={t.name}
                  onChange={e => changeTask(i, 'name', e.target.value)}
                />
                <input
                  placeholder="Description"
                  value={t.description}
                  onChange={e => changeTask(i, 'description', e.target.value)}
                />
                <input
                  type="date"
                  value={fmtDate(t.date)}
                  onChange={e => changeTask(i, 'date', e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Hours"
                  value={t.hours}
                  min="0"
                  onChange={e => changeTask(i, 'hours', e.target.value)}
                />
                <div className="cell actions">
                  <button
                    type="button"
                    onClick={() => removeTask(i)}
                    title="Remove Task"
                  >
                    <FaTrashAlt/>
                  </button>
                </div>
              </div>
            ))}
            {formData.tasks.length === 0 && (
              <div className="empty-hint">No tasks yet – add one above.</div>
            )}
          </section>

          {/* Expenses */}
          <section className="panel">
            <div className="section-head">
              <h3>Expenses</h3>
              <button type="button" className="btn-inline-add" onClick={addExpense}>
                <FaPlus/> Add Expense
              </button>
            </div>
            <div className="expenses-header">
              <div>Supplier</div>
              <div>Description</div>
              <div>Qty</div>
              <div>Unit Cost</div>
              <div>Mark-up %</div>
              <div>Base Total</div>
              <div>After Mark-up</div>
              <div></div>
            </div>
            {formData.expenses.map((ex, i) => (
              <div className="expense-row" key={i}>
                <div className="cell">
                  <select
                    value={ex.supplier}
                    onChange={e => changeExpense(i, 'supplier', e.target.value)}
                  >
                    <option value="">Supplier…</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="cell">
                  <input
                    placeholder="Description"
                    value={ex.description}
                    onChange={e => changeExpense(i, 'description', e.target.value)}
                  />
                </div>
                <div className="cell">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={ex.qty}
                    min="0"
                    onChange={e => changeExpense(i, 'qty', e.target.value)}
                  />
                </div>
                <div className="cell">
                  <input
                    type="number"
                    placeholder="Cost"
                    value={ex.cost}
                    min="0"
                    step="0.01"
                    onChange={e => changeExpense(i, 'cost', e.target.value)}
                  />
                </div>
                <div className="cell">
                  <input
                    type="number"
                    placeholder="%"
                    value={ex.markup}
                    min="0"
                    step="0.01"
                    onChange={e => changeExpense(i, 'markup', e.target.value)}
                  />
                </div>
                <div className="cell readonly">
                  <input
                    readOnly
                    value={(ex.base ?? ((parseFloat(ex.qty)||0)*(parseFloat(ex.cost)||0))).toFixed(2)}
                    title="Base total (qty * cost)"
                  />
                </div>
                <div className="cell readonly">
                  <input
                    readOnly
                    value={(ex.total ?? ((parseFloat(ex.qty)||0)*(parseFloat(ex.cost)||0)*(1+(parseFloat(ex.markup)||0)/100))).toFixed(2)}
                    title="After mark-up"
                  />
                </div>
                <div className="cell actions">
                  <button
                    type="button"
                    onClick={() => removeExpense(i)}
                    disabled={formData.expenses.length === 1}
                    title="Remove Expense"
                  ><FaTrashAlt/></button>
                </div>
              </div>
            ))}
            {formData.expenses.length === 0 && (
              <div className="empty-hint">No expenses yet – add one above.</div>
            )}
            <div className="expense-totals">
              <div className="metric">
                <span>Before Mark-up</span>
                <span className="val">R{totals.base}</span>
              </div>
              <div className="metric final">
                <span>After Mark-up</span>
                <span className="val">R{totals.final}</span>
              </div>
            </div>
          </section>

          {/* Files */}
          <section className="panel">
            <h3>Attachments</h3>
            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={openFileDialog}
              role="button"
              aria-label="Upload files"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') openFileDialog(); }}
            >
              <FaCloudUploadAlt size={32}/>
              <p>{dragActive ? 'Drop to upload' : 'Click or drag files here'}</p>
              <small>Images, PDF, CSV • Max 10 files • 5MB each</small>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.csv"
                multiple
                onChange={onFileInput}
                style={{ display: 'none' }}
              />
              <div className="file-types">
                <span>PNG</span><span>JPG</span><span>PDF</span><span>CSV</span>
              </div>
            </div>
            {previews.length > 0 && (
              <div className="thumbnail-row">
                {previews.map((p, i) => {
                  const generic = !isImage(p.name);
                  return (
                    <div key={i} className={`thumb-wrapper ${generic ? 'file-generic' : ''}`}>
                      {generic ? (
                        <div className="generic-label">
                          {isPdf(p.name) ? 'PDF' : isCsv(p.name) ? 'CSV' : 'FILE'}
                          <div className="g-name" title={p.name}>{p.name}</div>
                        </div>
                      ) : (
                        <img src={p.url} alt={p.name} className="thumb" />
                      )}
                      <button
                        type="button"
                        className="remove-thumb"
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${p.name}`}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Actions */}
          <section className="panel actions-panel">
            <div className="save-section">
              <button
                type="submit"
                className="btn-submit"
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Job'}
              </button>
              <button
                type="button"
                className="btn-clear"
                onClick={clearForm}
                disabled={saving}
              >
                Reset Changes
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={cancel}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </section>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={56}/>
          <p>Updating job…</p>
        </div>
      )}
    </div>
  );
}
